const ADMIN_TOKEN_KEY = 'admin_token';
const DEFAULT_TIMEOUT = 15000;

function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

async function handleResponse(response: Response): Promise<Response> {
  if (response.status === 401) {
    // Token invalido o expirado: limpiar sesion
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin/login')) {
      window.location.href = '/admin/login';
    }
    const errorText = await response.text().catch(() => '');
    let parsed: any = {};
    try { parsed = JSON.parse(errorText); } catch {}
    const error = new Error(parsed.message || 'Token admin invalido o expirado');
    (error as any).status = 401;
    throw error;
  }

  if (response.status >= 400) {
    const errorText = await response.text().catch(() => 'Error desconocido');
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }
    const error = new Error(errorData.message || `HTTP error: ${response.status}`);
    (error as any).status = response.status;
    (error as any).data = errorData;
    throw error;
  }

  return response;
}

export const adminHttpClient = {
  async fetch(url: string, options: RequestInit = {}): Promise<Response> {
    // El token viaja en el header `Authorization: Bearer ...` para evitar que
    // termine en logs CloudFront/nginx, `Referer` o browser history (vector que
    // tenia el viejo `?token=` en query string).
    const token = getToken();
    const headers = new Headers(options.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

    try {
      const response = await fetch(url, { ...options, headers, signal: controller.signal });
      return handleResponse(response);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutError = new Error(`Request timeout: ${url}`);
        (timeoutError as any).status = 0;
        throw timeoutError;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  },

  async get<T>(url: string): Promise<T> {
    const response = await this.fetch(url, { method: 'GET' });
    return response.json();
  },

  /** Descarga un binario (CSV, PDF, etc.) y devuelve `{ blob, filename }`. El filename viene del header Content-Disposition si está presente. */
  async getBlob(url: string): Promise<{ blob: Blob; filename: string | null }> {
    const response = await this.fetch(url, { method: 'GET' });
    const blob = await response.blob();
    const cd = response.headers.get('Content-Disposition') ?? '';
    const match = /filename="?([^";]+)"?/i.exec(cd);
    const filename = match ? match[1] : null;
    return { blob, filename };
  },

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.fetch(url, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: data ? { 'Content-Type': 'application/json' } : undefined,
    });
    return response.json();
  },

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.fetch(url, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: data ? { 'Content-Type': 'application/json' } : undefined,
    });
    return response.json();
  },

  async patch<T>(url: string, data: any): Promise<T> {
    const response = await this.fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: { 'Content-Type': 'application/json' },
    });
    return response.json();
  },

  async delete(url: string): Promise<void> {
    await this.fetch(url, { method: 'DELETE' });
  },
};
